/**
 * Warranty Template Detail Page - layout shell.
 *
 * Renders the back link, header, and the shared horizontal tab strip,
 * then delegates each section to a sub-route via <Outlet />:
 *   - /manage/warranties/templates/:uuid/overview
 *   - /manage/warranties/templates/:uuid/attributes
 *   - /manage/warranties/templates/:uuid/display
 *   - /manage/warranties/templates/:uuid/terms
 *   - /manage/warranties/templates/:uuid/rules
 *
 * Each sub-route is lazy-loaded in App.tsx so heavy panels (e.g. the
 * Rules tab + condition builder) only ship when navigated to.
 */

import { Suspense, useState } from 'react'
import { Link, Outlet, useParams, useNavigate } from 'react-router'
import {
  useWarrantyTemplate,
  useCloneWarrantyTemplate,
  useActivateWarrantyTemplate,
} from '@/admin/hooks/use-warranties'
import { AppShell } from '@/admin/components/app-shell'
import {
  ArrowLeft,
  Pencil,
  Copy,
  Zap,
  FileText,
  Layers,
  Columns3,
  Shield,
  Scale,
  MousePointerClick,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { WarrantyTemplateStatus } from '@/admin/types/warranties'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'
import { Tab } from '@/shared/components/ui/tab'
import { COVERAGE_LABELS, TYPE_LABELS } from './warranty-class-detail/constants'
import { EditTemplateDialog } from './warranty-class-detail/edit-template-dialog'

// ── Status Badge ────────────────────────────────────────────

function StatusBadge({ status }: { status: WarrantyTemplateStatus }) {
  const styles: Record<WarrantyTemplateStatus, string> = {
    DRAFT: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
    ACTIVE: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
    ARCHIVED: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
  }
  const s = styles[status] ?? styles.DRAFT
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[3px] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em]',
        s,
      )}
    >
      {status.toLowerCase()}
    </span>
  )
}

// ── Main Page ───────────────────────────────────────────────

export function WarrantyClassDetailPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: wc, isLoading } = useWarrantyTemplate(uuid ?? null)
  const cloneMutation = useCloneWarrantyTemplate()
  const activateMutation = useActivateWarrantyTemplate()

  const [editing, setEditing] = useState(false)

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="text-[13px] text-[var(--ink-3)]">{t('common.loading')}</p>
        </main>
      </AppShell>
    )
  }

  if (!wc) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="text-[13px] text-[var(--ink-3)]">Warranty template not found.</p>
        </main>
      </AppShell>
    )
  }

  const base = `/manage/warranties/templates/${uuid}`
  const tabs = [
    { label: 'Overview', path: `${base}/overview`, icon: FileText },
    { label: 'Attributes', path: `${base}/attributes`, icon: Layers },
    { label: 'Display', path: `${base}/display`, icon: Columns3 },
    { label: 'Terms & Conditions', path: `${base}/terms`, icon: Scale },
    { label: 'Rules', path: `${base}/rules`, icon: Shield },
    { label: 'Actions', path: `${base}/actions`, icon: MousePointerClick },
  ]

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* Back */}
        <Button
          variant="ghost"
          onClick={() => navigate('/manage/warranties/templates')}
          className="mb-6 flex h-auto cursor-pointer items-center gap-1 px-0 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('warrantyClasses.backToList', 'Back to Warranty Templates')}
        </Button>

        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            {/* Title row */}
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <h1 className="clara-title m-0 text-[var(--ink)]">{wc.name}</h1>
              <StatusBadge status={wc.status} />
            </div>
            {/* Subtitle: well-structured metadata line */}
            <p className="text-[13px] text-[var(--ink-3)]">
              {TYPE_LABELS[wc.warranty_type] ?? wc.warranty_type}
              <> &middot; {COVERAGE_LABELS[wc.coverage_scope] ?? wc.coverage_scope}</>
              {wc.policy_code && (
                <>
                  {' '}&middot;{' '}
                  <span className="font-mono tracking-[0.02em]">{wc.policy_code}</span>
                </>
              )}
              <>
                {' '}&middot;{' '}
                <span className="text-[var(--ink-2)]">
                  {wc.warranty_category?.name ?? 'Uncategorized'}
                </span>
              </>
              {wc.cloned_from && (
                <>
                  {' '}&middot; Cloned from{' '}
                  <Link
                    to={`/manage/warranties/templates/${wc.cloned_from.uuid}/overview`}
                    className="text-[var(--green)] hover:underline"
                  >
                    {wc.cloned_from.name}
                  </Link>
                </>
              )}
            </p>
            {/* Description */}
            {wc.description && (
              <p className="mt-3 max-w-[640px] text-[13px] leading-relaxed text-[var(--ink-2)]">
                {wc.description}
              </p>
            )}
          </div>

          {/* Right column: actions */}
          <div className="flex shrink-0 items-center gap-2">
            {wc.status === 'DRAFT' && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  if (uuid) {
                    activateMutation.mutate(uuid)
                  }
                }}
                disabled={activateMutation.isPending}
                className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em]"
              >
                <Zap className="h-3 w-3" />
                {activateMutation.isPending ? 'Activating…' : 'Activate'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (uuid) {
                  cloneMutation.mutate(uuid, {
                    onSuccess: (cloned) => {
                      navigate(`/manage/warranties/templates/${cloned.uuid}`)
                    },
                  })
                }
              }}
              disabled={cloneMutation.isPending}
              className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em]"
            >
              <Copy className="h-3 w-3" />
              {cloneMutation.isPending ? 'Cloning…' : 'Clone'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="warranty-template-edit-btn"
              className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em]"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </Button>
          </div>
        </div>

        <EditTemplateDialog open={editing} onOpenChange={setEditing} wc={wc} />

        {/* Tabs (route-driven, shared component) */}
        <div className="overflow-x-auto">
          <Tab tabs={tabs} />
        </div>

        {/* Active sub-route content */}
        <Suspense
          fallback={
            <p className="text-[13px] text-[var(--ink-3)]">{t('common.loading')}</p>
          }
        >
          <Outlet context={{ wc }} />
        </Suspense>
      </main>
    </AppShell>
  )
}
